/* eslint-disable no-prototype-builtins */
import CryptoJS from "crypto-js";
import HaptikSdk from "./haptik-sdk";
import environment from "@menu-environment";
import { ConnectTokenResponse, InitialConnectTokenResponse, StringDictionary } from "@menu/core/types";
import { CachedItem, RequestHelper, UrlBuilder, UrlUtils } from "@lib-shared/lib";
import { gotoUrl } from "@menu/core/lib";
import { keys, windowProps } from "@menu/core/constants";
import { MenuContextInterface } from "@menu/core/menu-context.interface";

const sha256 = async (message: string) => {
    // encode as UTF-8
    const msgBuffer = new TextEncoder().encode(message);

    // hash the message
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

    // convert ArrayBuffer to Array
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    // convert bytes to hex string                  
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
}

export async function addUserDataLayer(connectStore: { agreementId: any; connectId: any; email: any; CustomerNumber: any; }) {
    const existingUser = windowProps.mcgDataLayer?.hasOwnProperty("user") ? windowProps.mcgDataLayer.user : {};

    const hashedEmail = await sha256(connectStore.email);

    const user = {
        ...existingUser,
        billing_id: connectStore.agreementId,
        connect_id: connectStore.connectId,
        hashed_email: hashedEmail,
        login_type: "authenticated",
        user_id: connectStore.CustomerNumber,
    }

    windowProps.mcgDataLayer = {
        ...(windowProps.mcgDataLayer || {}),
        user
    }
}

export const connectCacheTimeToLiveInSecs = 86400; //24hrs;

const connectReauthTimeInSecs = 0;// 5 * 60;

export const connectCache = CachedItem.withoutPrefix(keys.connectAccess, true, connectCacheTimeToLiveInSecs);

const selfServiceSessionCache = CachedItem.withoutPrefix(keys.selfServiceSession);
const selfServiceUserCache = CachedItem.withoutPrefix(keys.selfServiceUser);

class ConnectApi {
    private _haptikSdk: HaptikSdk;
    private _cache = connectCache;

    private get _settings() {
        return this._context.settings;
    }

    private _useSelfServiceAfrica: boolean;
    private _context: MenuContextInterface;

    constructor(context: MenuContextInterface) {
        this._context = context;

        this._haptikSdk = new HaptikSdk(context.settings);

        this._useSelfServiceAfrica = context.settings?.useSelfServiceAfrica ?? false;

        (windowProps.siteUrl) = window.location.href;
        const arr = windowProps.siteUrl.split("/");
        windowProps.siteDomain = arr[2];
    }

    getSignInUrl() {
        const { culture } = this._context;

        if (this._useSelfServiceAfrica) {
            return `https://selfservice.dstvafrica.com/${culture?.culture}/mydstv-sign-in`;
        }

        const settings = this._settings?.connect;

        if (!settings?.loginUrl || !settings?.clientId) {
            return "";
        }

        const currentLocation = window.location.href.split("?")[0];

        const urlBuilder = new UrlBuilder(settings?.loginUrl)
            .appendParam("redirect_uri", currentLocation)
            .appendParam("client_id", settings?.clientId)
            .appendParam("usejwt", 'true')
            .appendParam("lang", culture?.code ?? "")
            .appendParam("prompt", "login");

        return urlBuilder.toString();
    }

    signIn() {
        const url = this.getSignInUrl();
        const openInNewWindow = this._useSelfServiceAfrica;

        gotoUrl(url, openInNewWindow);
    }

    async redeemAuthTokenAsync() {
        const paramKeys = {
            accessToken: "access_token",
            tokenType: "token_type",
            forceReAuth: "forceReAuth",
            forceAuth: "forceAuth"
        };

        const returnedKey = window.location.href;
        let index = returnedKey.lastIndexOf("#");
        const returnedKeyHasTokenType = returnedKey.includes(paramKeys.tokenType);
        const returnedKeyHasAccessToken = returnedKey.includes(paramKeys.accessToken);
        let search = "";

        if (returnedKeyHasTokenType) {
            search = returnedKey.substring(index + 1);
        }

        if (search === "" && returnedKeyHasAccessToken) {
            index = returnedKey.indexOf(paramKeys.accessToken);
            search = returnedKey.substring(index);
        }

        const searchParams = new URLSearchParams(search);
        const accessToken = searchParams.get(paramKeys.accessToken);

        const returnedKeyHasforceReAuth = searchParams.has(paramKeys.forceReAuth);
        const returnedKeyHasforceAuth = searchParams.has(paramKeys.forceAuth);

        if (!accessToken) {
            await this.authenticateAsync(returnedKeyHasforceReAuth, returnedKeyHasforceAuth, false);
            return;
        }

        const dict: StringDictionary = {};

        searchParams.forEach((value, key) => {
            dict[key] = value;
        });

        const connectToken = dict as InitialConnectTokenResponse;

        const expiresIn = connectToken.expires_in;

        if (expiresIn) {
            const date = new Date();
            date.setSeconds(date.getSeconds() + parseInt(expiresIn));
            connectToken.expires_time = date.toISOString();
        }

        this._cache.set(connectToken);

        // if (!environment.production) {
        //     return;
        // }

        await this.authenticateAsync(returnedKeyHasforceReAuth, returnedKeyHasforceAuth, true);
    }

    _skipReauth = () => {
        if (!this._context.connectToken || !connectReauthTimeInSecs) {
            return false;
        }

        //This will only work for React apps due to _context.connectToken check
        const authExpiry = (this._context.connectToken.time ?? 0) + (connectReauthTimeInSecs * 1000);

        return connectReauthTimeInSecs
            && authExpiry > 0
            && authExpiry < new Date().getTime();
    }

    private async _authenticateToken(payLoad: any) {
        const connectSettings = this._settings?.connect;
        const request = new RequestHelper(connectSettings.authUrl).withPath("signin");

        return await request.usingPromise().post<ConnectTokenResponse>(payLoad);
    }

    async authenticateAsync(forceReAuth: any, forceAuth: any, cleanUrl: boolean) {
        const haptikSettings = this._settings?.haptik;
        const connectSettings = this._settings?.connect;
        const connectStore = this._cache.value;

        const isBotEnabled = haptikSettings.bot;

        if (!(connectStore && connectSettings?.authUrl)) {
            if (isBotEnabled) {
                this._haptikSdk.guestSignIn();
            }
            return;
        }

        const skipReAuth = this._skipReauth();

        if (skipReAuth) {
            if (isBotEnabled) {
                this._haptikSdk.signIn(connectStore);
            }

            return;
        }

        const expiresTime = connectStore.expires_time.replaceAll("/");

        const payLoad = {
            accessToken: connectStore.access_token,
            expiresIn: connectStore.expires_in,
            idToken: connectStore.id_token,
            tokenType: connectStore.token_type,
            expiresTime: expiresTime,
            clientId: connectSettings.clientId,
            forceAuth: forceAuth,
            forceReAuth: forceReAuth
        };

        let data: any = undefined;

        try {
            const response = await this._authenticateToken(payLoad);
            data = response?.data;
        } catch (error) {
            console.error("Error authenticating", error);
        }

        if (cleanUrl) {
            this._removeHashFromUrl();
        }

        const isAuthenticated = data?.authenticated ?? false;

        if (!isAuthenticated && isBotEnabled) {
            this._haptikSdk.guestSignIn();
        }

        if (!isAuthenticated) {
            return;
        }

        const customerId = this.addCustomerToDMP(data?.CustomerNumber);

        this._context.updateConnectToken(data.access_token, data.id_token);

        data.CustomerNumber = customerId;
        addUserDataLayer(data)

        this._cache.set(data);

        if (isBotEnabled) {
            this._haptikSdk.signIn(data);
        }
    }

    private _removeHashFromUrl() {
        const urls = window.location.href.split("#");

        if (urls.length >= 2) {
            window.history.pushState("data", "", urls[0]);
        }
    }

    getSignOutUrl() {
        const settings = this._settings.connect;

        if (!settings.authUrl) {
            return "signout";
        }

        return UrlUtils.join(settings.authUrl, "signout");
    }

    async signOutAsync() {
        const url = this.getSignOutUrl();

        if (url) {
            const request = new RequestHelper(url);

            try {
                await request.withPath("").post({});
            } catch (error) {
                console.error("Error signing out of Connect", error);
            }
        }

        const haptikSettings = this._settings?.haptik;

        if (haptikSettings.bot) {
            this._haptikSdk.signOut();
        }

        selfServiceSessionCache.clear();
        selfServiceUserCache.clear();
        this._cache.clear();
    }

    addCustomerToDMP(customerNumber: string) {
        const permutive = windowProps.permutive;

        if (!permutive) {
            return customerNumber;
        }

        const encryptedId = CryptoJS.SHA256(customerNumber).toString(CryptoJS.enc.Hex);

        permutive.ready(function () {
            permutive.identify([{
                "id": encryptedId,
                "tag": "internal"
            }]);
        }, "initialised");

        return customerNumber;
    }
}

export default ConnectApi;
